import { useEffect, useState } from "react";
import type { AppSettings } from "@shared/core/types";
import { api } from "../lib/api";
import { useToastStore } from "../stores/toast";
import { blueprint } from "../blueprint";
import { PageHeader } from "../components/ui/page-header";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Field, Input } from "../components/ui/input";

interface SettingsResponse {
  settings: AppSettings;
  blueprint: {
    name: string;
    slug: string;
    domain: string;
    blueprintVersion: string;
    modules: string[];
  };
}

export function SettingsPage() {
  const push = useToastStore((s) => s.push);
  const [data, setData] = useState<SettingsResponse | null>(null);
  const [draft, setDraft] = useState<AppSettings | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<SettingsResponse>("/api/settings")
      .then((d) => {
        setData(d);
        setDraft(d.settings);
      })
      .catch(() => push("danger", "Impossible de charger les réglages"));
  }, [push]);

  const save = async () => {
    if (!draft || busy) return;
    setBusy(true);
    try {
      const saved = await api<AppSettings>("/api/settings", {
        method: "PUT",
        body: JSON.stringify(draft),
      });
      setDraft(saved);
      push("success", "Réglages enregistrés");
    } catch {
      push("danger", "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader title="Réglages" subtitle="Informations de l'organisation et état du projet." />

      <div className="settings-grid">
        <Card
          title="Organisation"
          footer={
            <Button variant="primary" onClick={save} disabled={busy || !draft}>
              Enregistrer
            </Button>
          }
        >
          {draft ? (
            <div className="form-grid">
              <Field label="Nom de l'organisation">
                <Input
                  value={draft.organisationName}
                  onChange={(e) => setDraft({ ...draft, organisationName: e.target.value })}
                />
              </Field>
              <Field label="Fuseau horaire" hint="Identifiant IANA, ex. America/Montreal.">
                <Input
                  value={draft.timezone}
                  onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
                />
              </Field>
              <Field label="Courriel de contact">
                <Input
                  type="email"
                  value={draft.contactEmail}
                  onChange={(e) => setDraft({ ...draft, contactEmail: e.target.value })}
                />
              </Field>
            </div>
          ) : (
            <p className="muted">Chargement…</p>
          )}
        </Card>

        <Card
          title="Factures et devis"
          subtitle="En-tête imprimé sur les documents : le nom de l'organisation ci-dessus, puis ces lignes."
          footer={
            <Button variant="primary" onClick={save} disabled={busy || !draft}>
              Enregistrer
            </Button>
          }
        >
          {draft ? (
            <div className="form-grid">
              <div className="form-grid__full">
                <Field
                  label="Raison sociale ou numéro d'entreprise"
                  hint="Imprimé sous le nom, ex. 9334-6971 Québec inc."
                >
                  <Input
                    value={draft.legalName}
                    onChange={(e) => setDraft({ ...draft, legalName: e.target.value })}
                    placeholder="ex. 9334-6971 Québec inc."
                  />
                </Field>
              </div>
              <div className="form-grid__full">
                <Field label="Adresse">
                  <Input
                    value={draft.address}
                    onChange={(e) => setDraft({ ...draft, address: e.target.value })}
                    placeholder="ex. 2147, boul. Talbot, Chicoutimi (Québec) G7H 8B3"
                  />
                </Field>
              </div>
              <Field label="Téléphone">
                <Input
                  type="tel"
                  value={draft.phone}
                  onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                  placeholder="ex. 418 602-3874"
                />
              </Field>
              <Field label="Numéro de TPS" hint="Vide = la ligne n'est pas imprimée.">
                <Input
                  value={draft.gstNumber}
                  onChange={(e) => setDraft({ ...draft, gstNumber: e.target.value })}
                />
              </Field>
              <Field label="Numéro de TVQ" hint="Vide = la ligne n'est pas imprimée.">
                <Input
                  value={draft.qstNumber}
                  onChange={(e) => setDraft({ ...draft, qstNumber: e.target.value })}
                />
              </Field>
            </div>
          ) : (
            <p className="muted">Chargement…</p>
          )}
        </Card>

        <Card title="Projet" subtitle="Figé au build depuis blueprint.json.">
          <dl className="kv">
            <dt>Nom</dt>
            <dd>{blueprint.name}</dd>
            <dt>Identifiant</dt>
            <dd>
              <code>{blueprint.slug}</code>
            </dd>
            <dt>Domaine</dt>
            <dd>{data?.blueprint.domain ?? "—"}</dd>
            <dt>Version du modèle</dt>
            <dd>{blueprint.blueprintVersion}</dd>
            <dt>Thème</dt>
            <dd>
              {blueprint.brand.mode === "dark" ? "Sombre" : "Clair"} ·{" "}
              <code>{blueprint.brand.primary}</code>
            </dd>
            <dt>Modules</dt>
            <dd className="kv__badges">
              {(data?.blueprint.modules ?? Object.keys(blueprint.modules)).map((m) => (
                <Badge key={m} variant="primary">
                  {m}
                </Badge>
              ))}
              {data && data.blueprint.modules.length === 0 ? (
                <span className="muted">aucun</span>
              ) : null}
            </dd>
          </dl>
        </Card>
      </div>
    </div>
  );
}
